import React, { useEffect, useState } from 'react';
import { LifeBuoy, Mail, Clock, Trash2, Check, ChevronDown, ChevronUp, AlertCircle, CheckCheck, Reply, Send, X, HelpCircle, RotateCcw, MessageSquare, ShieldCheck, ImagePlus, Loader2, Settings, Plus, Pencil } from 'lucide-react';
import { api, absoluteUrl } from '../../lib/api';
import RichTextEditor from '../../components/RichTextEditor';

const htmlIsEmpty = (h) => !h || !h.replace(/<img[^>]*>/gi, 'x').replace(/<[^>]+>/g, '').replace(/&nbsp;/g, '').trim();

const SOURCE_LABELS = {
  register: 'Registro (correo)',
  'register-confirm': 'Registro (completar perfil)',
  invitation: 'Invitación (crear cuenta)',
  member: 'Miembro (desde su perfil)',
};

const HELP_TYPE_LABELS = {
  login: 'No puede iniciar sesión',
  invitation: 'Enlace de invitación no funciona',
  photo: 'No puede subir su foto',
  profile: 'No sabe llenar/guardar el perfil',
  other: 'Otro / no está seguro',
};

// Plantillas de auto-respuesta con soluciones a errores comunes. Pensadas para
// miembros de 80+ años: lenguaje simple, pasos cortos y numerados.
const TEMPLATES = [
  {
    id: 'reset_password', emoji: '🔑', title: 'Olvidó su contraseña',
    subject: 'Cómo recuperar tu contraseña paso a paso',
    body: 'No te preocupes, vamos a recuperar tu contraseña juntos. Sigue estos pasos en orden:\n\n1. Abre la página de inicio de sesión del CMIC en tu navegador.\n2. Justo debajo de la casilla de la contraseña verás un texto azul que dice "¿Olvidaste tu contraseña?". Haz clic ahí.\n3. Escribe tu correo electrónico completo, todo junto y sin espacios (por ejemplo: nombre@gmail.com).\n4. Haz clic en el botón azul "Enviar".\n5. Abre tu correo electrónico y busca un mensaje del CMIC. Si no lo ves en unos minutos, revisa la carpeta de "Spam" o "Correo no deseado".\n6. Dentro de ese correo, haz clic en el botón o enlace azul para crear una contraseña nueva.\n7. Escribe tu nueva contraseña dos veces (debe tener al menos 8 letras o números) y guárdala.\n\nListo. Ahora ya puedes entrar con tu correo y tu nueva contraseña.',
  },
  {
    id: 'login_help', emoji: '🚪', title: 'No puede iniciar sesión',
    subject: 'Cómo entrar a tu cuenta correctamente',
    body: 'Vamos a revisar paso a paso cómo entrar a tu cuenta:\n\n1. Abre la página de inicio de sesión del CMIC.\n2. En la primera casilla escribe tu CORREO completo, todo junto y sin espacios (por ejemplo: nombre@gmail.com).\n3. En la segunda casilla escribe tu contraseña. Ten cuidado con las mayúsculas y minúsculas, porque sí importan.\n4. Si quieres revisar lo que escribiste, haz clic en el dibujo de un ojo 👁 que aparece dentro de la casilla de la contraseña.\n5. Haz clic en el botón azul "Iniciar sesión".\n\nSi te aparece un mensaje en color rojo:\n\n- Revisa que tu correo esté bien escrito.\n- Si no recuerdas tu contraseña, haz clic en "¿Olvidaste tu contraseña?" y crea una nueva.\n- Espera un minuto y vuelve a intentarlo.',
  },
  {
    id: 'invitation_expired', emoji: '✉️', title: 'Enlace de invitación expiró',
    subject: 'Te enviaremos un nuevo enlace para crear tu cuenta',
    body: 'Tu enlace de invitación dejó de funcionar porque ya pasó el tiempo en el que estaba disponible. No te preocupes, es muy fácil de resolver:\n\n1. Nosotros te enviaremos un enlace NUEVO a este mismo correo en las próximas horas.\n2. Cuando llegue, abre el correo y haz clic en el botón azul para crear tu cuenta.\n3. Llena tus datos y, sin cerrar la página, termina tu registro de una sola vez.\n\nUn par de recomendaciones:\n\n- Ten lista tu foto y tus datos antes de abrir el enlace, para terminar con calma.\n- El enlace nuevo también dura un tiempo limitado, así que úsalo el mismo día que lo recibas.\n\nGracias por tu paciencia.',
  },
  {
    id: 'photo_upload', emoji: '📷', title: 'No puede subir su foto',
    subject: 'Cómo subir tu foto de perfil paso a paso',
    body: 'Subir tu foto es muy sencillo. Hazlo con calma siguiendo cada paso:\n\n1. Busca la sección de la foto. Verás un círculo gris (donde irá tu foto) y, a un lado, un botón gris que dice "Subir imagen".\n2. Haz clic en el botón "Subir imagen".\n3. Se abrirá una ventana de tu computadora para buscar archivos. En Windows se llama "Explorador de archivos" y en Mac se llama "Finder".\n4. Del lado izquierdo de esa ventana busca dónde guardas tus fotos: normalmente la carpeta "Imágenes", "Descargas" o "Escritorio". (Si tu foto está en el celular, primero pásala a la computadora o realiza este paso desde el mismo celular.)\n5. Cuando veas tu foto, haz clic UNA vez sobre ella para seleccionarla (se pondrá azul o marcada).\n6. Haz clic en el botón "Abrir" en Windows, o "Elegir" / "Abrir" en Mac, que está en la esquina de esa ventana.\n7. Aparecerá una pantalla llamada "Ajustar foto". Ahí puedes acercar o alejar la imagen con la barra deslizante y moverla con el dedo o el ratón para centrar tu cara.\n8. Cuando te guste cómo se ve, haz clic en el botón azul "Guardar recorte".\n9. ¡Listo! Tu foto aparecerá en el círculo.\n\nTu foto debe ser una imagen tipo JPG o PNG y pesar menos de 5 MB.\n\nSi en este momento no lo logras, no te preocupes: puedes continuar SIN foto y agregarla más tarde desde tu perfil.',
  },
  {
    id: 'save_profile', emoji: '💾', title: 'No puede guardar el perfil',
    subject: 'Cómo guardar tu perfil paso a paso',
    body: 'Para guardar tu información correctamente:\n\n1. Revisa de arriba hacia abajo que las casillas marcadas con un asterisco rojo (*) estén llenas. Esas son obligatorias.\n2. Si alguna casilla está marcada en rojo, haz clic en ella y corrige lo que falta.\n3. Baja con la rueda del ratón (o deslizando con el dedo) hasta el final de la página.\n4. Haz clic en el botón azul "Guardar".\n5. Espera unos segundos sin cerrar la página, hasta que aparezca un mensaje verde de confirmación.\n\nSi el botón no responde, revisa que no haya ninguna casilla en rojo y vuelve a intentarlo.',
  },
  {
    id: 'fields_required', emoji: '⭐', title: 'Campos obligatorios faltantes',
    subject: 'Faltan algunos datos para terminar tu registro',
    body: 'Tu registro casi está listo. Solo faltan algunos datos OBLIGATORIOS, que son los que tienen un asterisco rojo (*):\n\n1. Sube hasta arriba del formulario.\n2. Llena tu nombre y tu apellido paterno.\n3. Escribe una contraseña de al menos 8 letras o números, y repítela en la casilla de abajo.\n4. Elige al menos un idioma en el que trabajas.\n5. Cuando termines, baja hasta el final y haz clic en el botón azul "Guardar".\n\nLas casillas que estén en rojo son las que faltan por corregir.',
  },
  {
    id: 'not_in_directory', emoji: '🕓', title: 'Cambios no aparecen aún',
    subject: 'Tus cambios están en revisión',
    body: 'Gracias por actualizar tu perfil. Te explicamos por qué algunos cambios todavía no se ven en el directorio:\n\n- Tu foto, tu teléfono y tu semblanza se publican de inmediato.\n- Tus idiomas, especialidades y modalidades los revisa primero el consejo directivo para confirmarlos.\n\nQué va a pasar ahora:\n\n1. El consejo revisará tus cambios en los próximos días.\n2. Cuando los apruebe, aparecerán automáticamente en el directorio.\n3. Te avisaremos por correo cuando estén publicados.\n\nNo necesitas volver a enviarlos. Gracias por tu paciencia.',
  },
  {
    id: 'email_not_received', emoji: '📭', title: 'No recibió el correo',
    subject: 'No encuentro el correo que me enviaron',
    body: 'Si no encuentras nuestro correo, vamos a buscarlo juntos:\n\n1. Abre tu correo electrónico (Gmail, Outlook, Hotmail, Yahoo, etc.).\n2. En el buscador de tu correo, escribe la palabra "CMIC" y presiona Enter.\n3. Revisa la carpeta de "Spam" o "Correo no deseado". A veces los correos llegan ahí por error.\n4. Espera unos minutos: en ocasiones el correo tarda un poco en llegar.\n\nSi después de esto sigues sin encontrarlo, respóndenos desde la página de soporte y con gusto te lo enviamos de nuevo.',
  },
  {
    id: 'browser_issue', emoji: '🌐', title: 'La página se ve mal / no carga',
    subject: 'Cómo hacer que la página funcione mejor',
    body: 'Si la página se ve rara, se queda en blanco o no carga bien, prueba esto en orden:\n\n1. Cierra por completo la pestaña y vuelve a abrir la página.\n2. Revisa que tengas conexión a internet (que otras páginas sí abran).\n3. Si puedes, usa el navegador Google Chrome, que es el que mejor funciona.\n4. Si usas teléfono o tableta, intenta girar la pantalla o acercar con dos dedos para ver mejor.\n5. Si nada de esto funciona, apaga y enciende tu dispositivo, y vuelve a entrar.\n\nCon estos pasos la página debería verse y funcionar correctamente.',
  },
  {
    id: 'change_email', emoji: '📨', title: 'Quiere cambiar su correo',
    subject: 'Cómo cambiar el correo de tu cuenta',
    body: 'El correo de tu cuenta lo cambiamos nosotros por ti, para que no tengas ningún problema:\n\n1. Respóndenos desde la página de soporte indicando cuál es tu correo NUEVO.\n2. Nosotros lo actualizaremos en el sistema.\n3. Te enviaremos un mensaje de confirmación cuando ya esté listo.\n\nMientras tanto, sigue usando tu correo actual para entrar a tu cuenta.',
  },
];

export default function AdminSupportPage() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [convOpen, setConvOpen] = useState(null);
  const [replyFor, setReplyFor] = useState(null);
  const [rSubject, setRSubject] = useState('');
  const [rMessage, setRMessage] = useState('');
  const [rImages, setRImages] = useState([]);
  const [rUploading, setRUploading] = useState(false);
  const [rSending, setRSending] = useState(false);
  const [statusFilter, setStatusFilter] = useState('unanswered');
  const [templates, setTemplates] = useState(TEMPLATES);
  const [mgrOpen, setMgrOpen] = useState(false);

  async function loadTemplates() {
    try {
      const { data } = await api.get('/admin/support/templates');
      if (Array.isArray(data)) setTemplates(data);
    } catch { /* keep fallback */ }
  }

  const tstatus = (t) => t.status || 'open';

  async function setStatus(id, status) {
    await api.post(`/admin/support/tickets/${id}/status`, { status }).catch(() => {});
    setTickets((t) => t.map((x) => (x.id === id ? { ...x, status } : x)));
  }

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/support/tickets');
      setTickets(data || []);
    } catch { setTickets([]); }
    setLoading(false);
  }

  useEffect(() => { load(); loadTemplates(); }, []);

  function openReply(t) {
    setReplyFor(t.id);
    setRSubject(t.last_reply?.subject || '');
    setRMessage(t.last_reply?.message || '');
    setRImages([]);
  }

  function applyTemplate(tpl) {
    setRSubject(tpl.subject);
    setRMessage(tpl.body);
  }

  async function uploadReplyImages(threadToken, fileList) {
    const files = Array.from(fileList || []);
    if (!files.length || !threadToken) return;
    setRUploading(true);
    try {
      for (const f of files.slice(0, 6 - rImages.length)) {
        const fd = new FormData();
        fd.append('file', f);
        const { data } = await api.post(`/support/thread/${threadToken}/upload-image`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        if (data?.url) setRImages((prev) => [...prev, data.url]);
      }
    } catch {
      window.alert('No se pudo subir la imagen. Usa JPG/PNG/WebP de máximo 5 MB.');
    }
    setRUploading(false);
  }

  async function sendReply(id) {
    if (htmlIsEmpty(rMessage) && rImages.length === 0) { window.alert('Escribe o elige una solución (o adjunta una imagen) antes de enviar.'); return; }
    setRSending(true);
    try {
      await api.post(`/admin/support/tickets/${id}/reply`, { subject: rSubject, html: htmlIsEmpty(rMessage) ? '' : rMessage, images: rImages });
      setReplyFor(null);
      setRSubject(''); setRMessage(''); setRImages([]);
      await load();          // refresh with the real conversation + status (no auto-resolve)
      setConvOpen(id);       // show the updated history right away
    } catch (e) {
      window.alert(e.response?.data?.detail || 'No se pudo enviar el correo.');
    }
    setRSending(false);
  }

  async function markRead(id) {
    await api.post(`/admin/support/tickets/${id}/mark-read`).catch(() => {});
    setTickets((t) => t.map((x) => (x.id === id ? { ...x, read: true } : x)));
  }

  async function markAllRead() {
    await api.post('/admin/support/tickets/mark-all-read').catch(() => {});
    setTickets((t) => t.map((x) => ({ ...x, read: true })));
  }

  async function remove(id) {
    if (!window.confirm('¿Eliminar este ticket de soporte?')) return;
    await api.delete(`/admin/support/tickets/${id}`).catch(() => {});
    setTickets((t) => t.filter((x) => x.id !== id));
  }

  const unread = tickets.filter((t) => !t.read).length;
  const needsReply = tickets.filter((t) => t.needs_reply).length;
  const openCount = tickets.filter((t) => tstatus(t) === 'open').length;
  const resolvedCount = tickets.filter((t) => tstatus(t) === 'resolved').length;
  const visible = tickets.filter((t) => {
    if (statusFilter === 'unanswered') return t.needs_reply;
    return statusFilter === 'all' || tstatus(t) === statusFilter;
  });

  return (
    <div data-testid="admin-support-page">
      <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-charcoal-900 flex items-center gap-2">
            <LifeBuoy size={24} className="text-cmic-600" /> Soporte
          </h1>
          <p className="text-sm text-charcoal-500 mt-1">
            Tickets enviados por usuarios con problemas al registrarse.
            {needsReply > 0 && <span className="ml-1 font-semibold text-rose-600">{needsReply} sin responder.</span>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setMgrOpen(true)}
            data-testid="support-manage-templates-btn"
            className="inline-flex items-center gap-2 text-sm font-medium text-charcoal-600 hover:text-cmic-700 bg-white border border-gray-200 rounded-lg px-3 py-2"
          >
            <Settings size={16} /> Gestionar respuestas
          </button>
          {unread > 0 && (
            <button
              onClick={markAllRead}
              data-testid="support-mark-all-read"
              className="inline-flex items-center gap-2 text-sm font-medium text-charcoal-600 hover:text-cmic-700 bg-white border border-gray-200 rounded-lg px-3 py-2"
            >
              <CheckCheck size={16} /> Marcar todo como leído
            </button>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2 mb-4 flex-wrap" data-testid="support-status-filter">
        {[
          { id: 'unanswered', label: 'Sin responder', count: needsReply },
          { id: 'open', label: 'Abiertos', count: openCount },
          { id: 'resolved', label: 'Resueltos', count: resolvedCount },
          { id: 'all', label: 'Todos', count: tickets.length },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setStatusFilter(tab.id)}
            data-testid={`support-filter-${tab.id}`}
            className={`inline-flex items-center gap-1.5 text-sm font-medium rounded-full px-4 py-1.5 border transition-colors ${
              statusFilter === tab.id ? 'bg-cmic-600 text-white border-cmic-600' : 'bg-white text-charcoal-600 border-gray-200 hover:border-cmic-300'
            }`}
          >
            {tab.label} <span className={`text-xs ${statusFilter === tab.id ? 'text-white/80' : 'text-charcoal-400'}`}>({tab.count})</span>
          </button>
        ))}
      </div>

      {loading ? (
        <div className="py-20 flex justify-center"><div className="w-8 h-8 border-3 border-cmic-200 border-t-cmic-500 rounded-full animate-spin" /></div>
      ) : visible.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-2xl border border-gray-100" data-testid="support-empty">
          <LifeBuoy size={40} className="text-charcoal-300 mx-auto mb-3" />
          <p className="text-charcoal-500">{tickets.length === 0 ? 'No hay tickets de soporte por ahora.' : 'No hay tickets en esta vista.'}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {visible.map((t) => {
            const isOpen = expanded === t.id;
            return (
              <div
                key={t.id}
                data-testid={`support-ticket-${t.id}`}
                className={`bg-white rounded-2xl border ${t.read ? 'border-gray-100' : 'border-cmic-200 ring-1 ring-cmic-100'} overflow-hidden`}
              >
                <div className="p-4 sm:p-5">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        {!t.read && <span className="w-2 h-2 rounded-full bg-rose-500 flex-shrink-0" />}
                        {t.name && (
                          <span className="font-bold text-charcoal-900" data-testid={`support-ticket-name-${t.id}`}>{t.name}</span>
                        )}
                        <span className="font-semibold text-charcoal-800 inline-flex items-center gap-1.5">
                          <Mail size={14} className="text-charcoal-400" />{t.email || '— sin correo —'}
                        </span>
                        <span className="text-[10px] uppercase tracking-wider font-bold bg-cmic-50 text-cmic-700 border border-cmic-100 rounded px-1.5 py-0.5">
                          {SOURCE_LABELS[t.source] || t.source}
                        </span>
                        <span data-testid={`support-status-badge-${t.id}`} className={`text-[10px] uppercase tracking-wider font-bold rounded px-1.5 py-0.5 border ${
                          tstatus(t) === 'resolved' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-amber-50 text-amber-700 border-amber-200'
                        }`}>
                          {tstatus(t) === 'resolved' ? 'Resuelto' : 'Abierto'}
                        </span>
                        {t.needs_reply && (
                          <span data-testid={`support-needs-reply-${t.id}`} className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold rounded px-1.5 py-0.5 bg-rose-50 text-rose-700 border border-rose-200">
                            <Reply size={11} /> Esperando tu respuesta
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-charcoal-400 mt-1 inline-flex items-center gap-1.5">
                        <Clock size={12} />{new Date(t.created_at).toLocaleString('es-MX')}
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      {tstatus(t) === 'open' ? (
                        <button onClick={() => setStatus(t.id, 'resolved')} data-testid={`support-resolve-${t.id}`} title="Marcar como resuelto"
                          className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 rounded-lg px-2.5 py-1.5"><CheckCheck size={14} /> Resuelto</button>
                      ) : (
                        <button onClick={() => setStatus(t.id, 'open')} data-testid={`support-reopen-${t.id}`} title="Reabrir"
                          className="inline-flex items-center gap-1 text-xs font-semibold text-amber-700 bg-amber-50 hover:bg-amber-100 border border-amber-200 rounded-lg px-2.5 py-1.5"><RotateCcw size={14} /> Reabrir</button>
                      )}
                      {!t.read && (
                        <button onClick={() => markRead(t.id)} data-testid={`support-mark-read-${t.id}`} title="Marcar como leído"
                          className="p-2 rounded-lg text-emerald-600 hover:bg-emerald-50"><Check size={16} /></button>
                      )}
                      <button onClick={() => remove(t.id)} data-testid={`support-delete-${t.id}`} title="Eliminar"
                        className="p-2 rounded-lg text-rose-500 hover:bg-rose-50"><Trash2 size={16} /></button>
                    </div>
                  </div>

                  {t.error_message && (
                    <div className="mt-3 flex items-start gap-2 text-sm bg-red-50 border border-red-200 text-red-700 rounded-xl px-3 py-2">
                      <AlertCircle size={15} className="mt-0.5 flex-shrink-0" />
                      <span><strong>Error mostrado:</strong> {t.error_message}</span>
                    </div>
                  )}
                  {t.message && (
                    <div className="mt-3 text-sm text-charcoal-700 bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 whitespace-pre-wrap">
                      {t.message}
                    </div>
                  )}
                  {(t.help_types || []).length > 0 && (
                    <div className="mt-3 flex flex-wrap items-center gap-2" data-testid={`support-help-types-${t.id}`}>
                      <span className="inline-flex items-center gap-1 text-xs font-semibold text-charcoal-500"><HelpCircle size={13} /> Necesita ayuda con:</span>
                      {t.help_types.map((h) => (
                        <span key={h} className="text-[11px] font-medium bg-cmic-50 text-cmic-700 border border-cmic-100 rounded-full px-2 py-0.5">
                          {HELP_TYPE_LABELS[h] || h}
                        </span>
                      ))}
                    </div>
                  )}

                  {t.last_reply && (
                    <div className="mt-3 flex items-start gap-2 text-sm bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-xl px-3 py-2" data-testid={`support-replied-${t.id}`}>
                      <CheckCheck size={15} className="mt-0.5 flex-shrink-0" />
                      <span>Respuesta enviada a <strong>{t.last_reply.to_email}</strong> el {new Date(t.last_reply.sent_at).toLocaleString('es-MX')}.</span>
                    </div>
                  )}

                  <div className="mt-3 flex flex-wrap items-center gap-4">
                    {t.email && (
                      <button
                        onClick={() => (replyFor === t.id ? setReplyFor(null) : openReply(t))}
                        data-testid={`support-reply-toggle-${t.id}`}
                        className="inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-cmic-600 hover:bg-cmic-700 rounded-lg px-3 py-1.5"
                      >
                        <Reply size={14} /> {t.last_reply ? 'Responder de nuevo' : 'Responder con una solución'}
                      </button>
                    )}
                    <button
                      onClick={() => setExpanded(isOpen ? null : t.id)}
                      data-testid={`support-toggle-log-${t.id}`}
                      className="inline-flex items-center gap-1.5 text-xs font-medium text-cmic-600 hover:text-cmic-700"
                    >
                      {isOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                      Registro de clicks ({(t.click_log || []).length})
                    </button>
                    {(t.messages || []).length > 0 && (
                      <button
                        onClick={() => setConvOpen(convOpen === t.id ? null : t.id)}
                        data-testid={`support-toggle-conv-${t.id}`}
                        className="inline-flex items-center gap-1.5 text-xs font-medium text-cmic-600 hover:text-cmic-700"
                      >
                        <MessageSquare size={14} /> Conversación ({t.messages.length})
                      </button>
                    )}
                  </div>

                  {convOpen === t.id && (
                    <div className="mt-3 rounded-xl border border-gray-100 bg-gray-50 p-3 space-y-2" data-testid={`support-conv-${t.id}`}>
                      {(t.messages || []).map((m) => {
                        const isAdmin = m.from === 'admin';
                        return (
                          <div key={m.id} className={`flex ${isAdmin ? 'justify-end' : 'justify-start'}`}>
                            <div className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm ${isAdmin ? 'bg-cmic-600 text-white' : 'bg-white border border-gray-200 text-charcoal-800'}`}>
                              <div className={`flex items-center gap-1 text-[10px] font-semibold mb-0.5 ${isAdmin ? 'text-white/80' : 'text-cmic-700'}`}>
                                {isAdmin ? <><ShieldCheck size={11} /> Soporte</> : <><MessageSquare size={11} /> Usuario</>}
                              </div>
                              {m.subject && <div className="font-semibold mb-0.5">{m.subject}</div>}
                              {m.html
                                ? <div className={`support-html leading-relaxed ${isAdmin ? 'support-html-invert' : ''}`} dangerouslySetInnerHTML={{ __html: m.html }} />
                                : (m.text && <div className="whitespace-pre-wrap leading-relaxed">{m.text}</div>)}
                              {Array.isArray(m.images) && m.images.length > 0 && (
                                <div className="flex flex-wrap gap-1.5 mt-1.5" data-testid={`support-conv-images-${m.id}`}>
                                  {m.images.map((u, i) => (
                                    <a key={i} href={absoluteUrl(u)} target="_blank" rel="noreferrer">
                                      <img src={absoluteUrl(u)} alt="Adjunto" className="max-w-[120px] max-h-[120px] rounded-md object-cover border border-black/10" />
                                    </a>
                                  ))}
                                </div>
                              )}
                              {m.created_at && <div className={`text-[10px] mt-1 ${isAdmin ? 'text-white/70' : 'text-charcoal-400'}`}>{new Date(m.created_at).toLocaleString('es-MX')}</div>}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {replyFor === t.id && (
                    <div className="mt-3 rounded-xl border border-cmic-200 bg-cmic-50/40 p-4" data-testid={`support-reply-panel-${t.id}`}>
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-xs font-semibold text-charcoal-700">Elige una solución lista o escribe la tuya:</p>
                        <button onClick={() => setReplyFor(null)} className="p-1 text-charcoal-400 hover:text-charcoal-700"><X size={15} /></button>
                      </div>
                      <div className="flex flex-wrap gap-2 mb-3">
                        {templates.map((tpl) => (
                          <button
                            key={tpl.id}
                            type="button"
                            onClick={() => applyTemplate(tpl)}
                            data-testid={`support-template-${tpl.id}`}
                            className="inline-flex items-center gap-1.5 text-xs font-medium bg-white border border-gray-200 hover:border-cmic-400 hover:bg-cmic-50 text-charcoal-700 rounded-full px-3 py-1.5 transition-colors"
                            title={tpl.subject}
                          >
                            <span>{tpl.emoji}</span> {tpl.title}
                          </button>
                        ))}
                      </div>
                      <input
                        type="text"
                        value={rSubject}
                        onChange={(e) => setRSubject(e.target.value)}
                        data-testid={`support-reply-subject-${t.id}`}
                        placeholder="Asunto del correo (ej.: Cómo recuperar tu contraseña)"
                        className="w-full mb-2 rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cmic-500/30"
                      />
                      <div data-testid={`support-reply-message-${t.id}`}>
                        <RichTextEditor
                          value={rMessage}
                          onChange={setRMessage}
                          placeholder="Escribe la solución. Puedes dar formato e insertar imágenes en línea con el botón de imagen de la barra."
                          minHeight={200}
                        />
                      </div>
                      {rImages.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-2" data-testid={`support-reply-image-previews-${t.id}`}>
                          {rImages.map((u, i) => (
                            <div key={i} className="relative">
                              <img src={absoluteUrl(u)} alt="Adjunto" className="w-16 h-16 rounded-lg object-cover border border-gray-200" />
                              <button
                                type="button"
                                onClick={() => setRImages((prev) => prev.filter((_, idx) => idx !== i))}
                                data-testid={`support-reply-remove-image-${i}`}
                                className="absolute -top-2 -right-2 bg-rose-600 text-white rounded-full w-5 h-5 flex items-center justify-center shadow"
                                title="Quitar"
                              ><X size={12} /></button>
                            </div>
                          ))}
                        </div>
                      )}
                      <label
                        data-testid={`support-reply-attach-${t.id}`}
                        className={`inline-flex items-center gap-2 mt-2 text-xs font-medium rounded-lg px-3 py-2 cursor-pointer ${rUploading || rImages.length >= 6 ? 'opacity-50 pointer-events-none' : 'text-cmic-700 hover:bg-cmic-50'}`}
                      >
                        {rUploading ? <Loader2 size={15} className="animate-spin" /> : <ImagePlus size={15} />} Adjuntar imagen
                        <input
                          type="file"
                          accept="image/*"
                          multiple
                          className="hidden"
                          onChange={(e) => { uploadReplyImages(t.thread_token, e.target.files); e.target.value = ''; }}
                        />
                      </label>
                      <div className="mt-2 flex items-center justify-between gap-3 flex-wrap">
                        <p className="text-[11px] text-charcoal-500 flex items-center gap-1">
                          <AlertCircle size={12} /> El correo se envía a <strong>{t.email}</strong> con la leyenda "NO responder" (es de un solo sentido).
                        </p>
                        <button
                          onClick={() => sendReply(t.id)}
                          disabled={rSending || rUploading || (htmlIsEmpty(rMessage) && rImages.length === 0)}
                          data-testid={`support-reply-send-${t.id}`}
                          className="inline-flex items-center gap-2 bg-cmic-600 hover:bg-cmic-700 text-white text-sm font-semibold rounded-lg px-4 py-2 disabled:opacity-50"
                        >
                          <Send size={15} /> {rSending ? 'Enviando…' : 'Enviar al correo del usuario'}
                        </button>
                      </div>
                    </div>
                  )}
                  {isOpen && (
                    <div className="mt-2 max-h-64 overflow-auto rounded-xl border border-gray-100 bg-charcoal-800 text-charcoal-100 p-3 font-mono text-[11px] leading-relaxed" data-testid={`support-log-${t.id}`}>
                      {(t.click_log || []).length === 0 ? (
                        <p className="text-charcoal-400">Sin registro de clicks.</p>
                      ) : (
                        (t.click_log || []).map((e, i) => (
                          <div key={i} className="flex gap-2">
                            <span className="text-charcoal-400 flex-shrink-0">{(e.t || '').slice(11, 19)}</span>
                            <span className="text-cmic-300">{e.label}</span>
                            <span className="truncate">{e.target || e.message || ''}</span>
                          </div>
                        ))
                      )}
                      <div className="mt-2 pt-2 border-t border-charcoal-700 text-charcoal-400">
                        {t.page_url && <div className="truncate">URL: {t.page_url}</div>}
                        {t.user_agent && <div className="truncate">UA: {t.user_agent}</div>}
                        {t.client_ip && <div>IP: {t.client_ip}</div>}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
      {mgrOpen && <TemplateManager templates={templates} onClose={() => setMgrOpen(false)} onChanged={loadTemplates} />}
    </div>
  );
}

function TemplateManager({ templates, onClose, onChanged }) {
  const [editing, setEditing] = useState(null); // template object or 'new'
  const [emoji, setEmoji] = useState('');
  const [title, setTitle] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [saving, setSaving] = useState(false);

  function startNew() { setEditing('new'); setEmoji('💬'); setTitle(''); setSubject(''); setBody(''); }
  function startEdit(t) { setEditing(t); setEmoji(t.emoji || ''); setTitle(t.title || ''); setSubject(t.subject || ''); setBody(t.body || ''); }

  async function save() {
    if (!title.trim()) { window.alert('El título es obligatorio.'); return; }
    setSaving(true);
    try {
      const payload = { emoji, title, subject, body };
      if (editing === 'new') await api.post('/admin/support/templates', payload);
      else await api.put(`/admin/support/templates/${editing.id}`, payload);
      setEditing(null);
      await onChanged();
    } catch (e) {
      window.alert(e.response?.data?.detail || 'No se pudo guardar la respuesta.');
    }
    setSaving(false);
  }

  async function remove(t) {
    if (!window.confirm(`¿Eliminar la respuesta "${t.title}"?`)) return;
    try { await api.delete(`/admin/support/templates/${t.id}`); await onChanged(); }
    catch { window.alert('No se pudo eliminar.'); }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center overflow-y-auto p-4" data-testid="support-template-manager">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl my-8">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h2 className="font-bold text-charcoal-900">Respuestas predefinidas</h2>
          <button onClick={onClose} data-testid="support-template-manager-close" className="p-1.5 text-charcoal-400 hover:text-charcoal-700"><X size={18} /></button>
        </div>

        {editing ? (
          <div className="p-5 space-y-3">
            <div className="flex gap-2">
              <input value={emoji} onChange={(e) => setEmoji(e.target.value)} maxLength={4} placeholder="😀" data-testid="tpl-emoji" className="w-16 text-center rounded-lg border border-gray-200 px-2 py-2 text-lg" />
              <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Título corto (ej.: Olvidó su contraseña)" data-testid="tpl-title" className="flex-1 rounded-lg border border-gray-200 px-3 py-2 text-sm" />
            </div>
            <input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Asunto del correo" data-testid="tpl-subject" className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm" />
            <div data-testid="tpl-body"><RichTextEditor value={body} onChange={setBody} placeholder="Cuerpo de la respuesta. Puedes dar formato e insertar imágenes." minHeight={200} /></div>
            <div className="flex items-center justify-end gap-2 pt-1">
              <button onClick={() => setEditing(null)} className="px-4 py-2 text-sm font-medium text-charcoal-600 hover:bg-gray-100 rounded-lg">Cancelar</button>
              <button onClick={save} disabled={saving} data-testid="tpl-save" className="inline-flex items-center gap-2 bg-cmic-600 hover:bg-cmic-700 text-white text-sm font-semibold rounded-lg px-4 py-2 disabled:opacity-50">
                {saving ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />} Guardar
              </button>
            </div>
          </div>
        ) : (
          <div className="p-5">
            <button onClick={startNew} data-testid="tpl-new" className="inline-flex items-center gap-2 mb-4 bg-cmic-600 hover:bg-cmic-700 text-white text-sm font-semibold rounded-lg px-4 py-2">
              <Plus size={15} /> Nueva respuesta
            </button>
            <div className="space-y-2 max-h-[55vh] overflow-y-auto">
              {templates.map((t) => (
                <div key={t.id} className="flex items-center gap-3 border border-gray-100 rounded-xl px-3 py-2" data-testid={`tpl-row-${t.id}`}>
                  <span className="text-xl">{t.emoji}</span>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-charcoal-800 text-sm truncate">{t.title}</div>
                    <div className="text-xs text-charcoal-400 truncate">{t.subject}</div>
                  </div>
                  <button onClick={() => startEdit(t)} data-testid={`tpl-edit-${t.id}`} className="p-2 text-cmic-600 hover:bg-cmic-50 rounded-lg"><Pencil size={15} /></button>
                  <button onClick={() => remove(t)} data-testid={`tpl-delete-${t.id}`} className="p-2 text-rose-500 hover:bg-rose-50 rounded-lg"><Trash2 size={15} /></button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
